import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query/keys'

/**
 * Delay before the follow-up list-query invalidation fires after a
 * `create_object` tool_result/tool_receipt. Rationale: background enrichment
 * (Google Places lookup + R2 photo upload) typically completes 2-5s after
 * the synchronous create returns. We re-invalidate at the receipt moment AND
 * after this delay so the unenriched snapshot doesn't linger up to the
 * 30s staleTime. Complements the post-stream 4/10/30/60s enrichment timers
 * in ChatBox. See #2458.
 */
export const ENRICHMENT_FOLLOWUP_MS = 6000

/**
 * Synthetic-key prefix used when an SSE `tool_start` event arrives without a
 * concrete tool_use id (older agents / synthetic emissions). Keyed by ordinal
 * position so subsequent `tool_result` events without an id fall back to the
 * same synthesised key.
 */
const SYNTHETIC_TOOL_USE_PREFIX = '__synthetic_create_'

/**
 * Bookkeeping helper for correlating `create_object` tool_start events to
 * their matching tool_result / tool_receipt events. Replaces the previous
 * FIFO array (#2458 follow-up): when two `create_object` calls execute in
 * parallel and results return out of order, FIFO mis-correlates the receipt
 * to the wrong objectType, firing the targeted invalidation on the wrong
 * list cache.
 *
 * - `register(toolUseId, objectType)` is called on `tool_start`.
 * - `consume(toolUseId)` is called on `tool_result` / `tool_receipt`.
 * - `consumeAny()` is a defensive fallback for the legacy case where neither
 *   event carries an id; falls back to the oldest pending entry.
 */
/**
 * Which xavior_api actions mutate an object's list and therefore need their
 * objectType tracked for targeted list-query invalidation + silent-turn
 * "Calendar updated" reporting. Covers create_object AND update_object (amends)
 * — a rescheduled/edited event should refresh the list and confirm just like a
 * freshly created one.
 */
export function isTrackedMutation(action: string | undefined): boolean {
  return action === 'create_object' || action === 'update_object'
}

export interface PendingCreateRegistry {
  register(toolUseId: string | undefined, objectType: string, ordinal: number): string
  consume(toolUseId: string | undefined): string | undefined
  consumeAny(): string | undefined
  size(): number
}

export function createPendingCreateRegistry(): PendingCreateRegistry {
  const map = new Map<string, string>()
  // Insertion order is preserved by Map; `consumeAny()` uses the first key.
  return {
    register(toolUseId, objectType, ordinal) {
      const key = toolUseId && toolUseId.length > 0
        ? toolUseId
        : `${SYNTHETIC_TOOL_USE_PREFIX}${ordinal}`
      map.set(key, objectType)
      return key
    },
    consume(toolUseId) {
      if (!toolUseId) return undefined
      const value = map.get(toolUseId)
      if (value !== undefined) {
        map.delete(toolUseId)
        return value
      }
      return undefined
    },
    consumeAny() {
      const firstKey = map.keys().next()
      if (firstKey.done) return undefined
      const value = map.get(firstKey.value)
      map.delete(firstKey.value)
      return value
    },
    size() {
      return map.size
    },
  }
}

/**
 * Fire the targeted list-query invalidation pair for a successful
 * create_object call: an immediate refetch + a delayed one ~6s later to
 * catch background enrichment (Google Places lookup, R2 photo upload).
 *
 * Returns a handle to the pending follow-up timer so callers can cancel it
 * if the hook unmounts before it fires (avoids a wasted refetch on a list
 * the user no longer views).
 */
export function scheduleObjectListInvalidation(
  queryClient: QueryClient,
  objectType: string,
): ReturnType<typeof setTimeout> {
  const opts = {
    queryKey: queryKeys.objects.byType(objectType),
    refetchType: 'all' as const,
  }
  queryClient.invalidateQueries(opts)
  return setTimeout(() => {
    queryClient.invalidateQueries(opts)
  }, ENRICHMENT_FOLLOWUP_MS)
}
